import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { AuthConfig, AUTH_CONFIG, OAuthService } from 'angular-oauth2-oidc';
import { defer, EMPTY, firstValueFrom, Observable, of, ReplaySubject, timer } from "rxjs";
import { catchError, concatMap, filter, map, take, timeout } from "rxjs/operators";
import { ISecureStorage } from "../../classes/interfaces/isecurestorage";
import { withSpinner } from "../../extensions/withSpinner.extension";
import { Claims, Token } from "../../types";
import { API_ROOT, SECURE_LOCAL_STORAGE } from "../../types/constants/appconstants";

@Injectable({
    "providedIn": "root"
})
export class AuthService {
    private http = inject(HttpClient);
    private apiRoot = inject(API_ROOT);
    private oAuthService = inject(OAuthService);
    private authConfig = inject<AuthConfig>(AUTH_CONFIG);
    private storage = inject<ISecureStorage>(SECURE_LOCAL_STORAGE);
    private getTokenUrl = this.apiRoot + "api/token";
    private storageKey: string = "Auth";

    private loggedInSubject$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
    private token: Token = null;

    private async configure() {
        this.oAuthService.configure(this.authConfig);
        this.oAuthService.setupAutomaticSilentRefresh();
    
        // Try login, but do NOT redirect automatically
        await this.oAuthService.loadDiscoveryDocumentAndTryLogin();
    
        const isLoggedIn =
            this.oAuthService.hasValidAccessToken() &&
            this.oAuthService.hasValidIdToken();
    
        this.loggedInSubject$.next(isLoggedIn);
    
        if (isLoggedIn) {
            const token = await firstValueFrom(this.getToken());
            this.token = token;
        }
    }    

    initialize() {
        return this.configure().then(async () => {
            const loggedIn = await firstValueFrom(this.isUserLoggedIn());
    
            if (!loggedIn) {
                console.log('app initializer');
                this.login(); // safe now — only called once
            }
        });
    }    

    login(): void {
        this.oAuthService.initLoginFlow();
    }

    isUserLoggedIn(): Observable<boolean> {
        let isLoggedIn = this.loggedInSubject$.pipe(take(1));
        return isLoggedIn;
    }

    /**
     * Resolves once a non-empty OAuth access token is available.
     * Covers the short window after authorization code exchange where HTTP calls
     * would otherwise send Authorization: Bearer null.
     */
    whenAccessTokenReady(): Observable<string> {
        return defer(() => {
            const existing = this.oAuthService.getAccessToken();
            if (existing) {
                return of(existing);
            }
            return timer(0, 25).pipe(
                map(() => this.oAuthService.getAccessToken()),
                filter((t): t is string => !!t && t.length > 0),
                take(1),
                timeout(30_000),
                catchError(() => EMPTY)
            );
        });
    }

    hasClaim(requiredClaim: Claims): Observable<boolean> {
        return of(this.token.claims.some((c) => c.type === Claims[requiredClaim]) ? true : false);
    }

    hasClaims(requiredClaims: Claims[]): boolean {
        let ret: boolean = true;
        requiredClaims.forEach(claim => {
            if(ret)
                ret = this.token.claims.some((c) => c.type === Claims[claim]);
        });
        return ret;
    }

    getClaimValue<T>(requiredClaim: Claims): T {
        let claimValue = this.token.claims.find((claim) => claim.type.endsWith(Claims[requiredClaim])).value;      
        if (claimValue === "True" || claimValue === "False") {
            return JSON.parse(claimValue.toLowerCase());
        }

        return claimValue as any;
    }

    private getToken(): Observable<Token> {
        return this.loggedInSubject$.pipe(
            take(1),
            concatMap(isLoggedIn => {
                if (isLoggedIn) {
                    var cacheToken = this.storage.getItem<Token>(this.storageKey) as Token;

                    if (cacheToken && cacheToken?.expirationTime > Math.floor(Date.now() / 1000)) {
                        this.token = cacheToken;
                        return of(this.token);
                    }

                    let accessToken: string = this.oAuthService.getAccessToken() ?? '';
                    let tokenClaims: any;

                    try {
                        let tokenParts = accessToken.split('.');
                        tokenClaims = JSON.parse(window.atob(tokenParts[1]));
                    } catch (e) {
                        console.log('Error parsing token:', e);
                        return of(null);
                    }

                    return withSpinner(this.http).get<Token>(this.getTokenUrl).pipe(
                        map((token: Token) => {
                            this.token = {
                                claims: token.claims,
                                issuedAtTime: tokenClaims.iat || 0,
                                notValidBeforeTime: tokenClaims.nbf || 0,
                                expirationTime: tokenClaims.exp || 0
                            }

                            const expirationMilliseconds = Date.now() + (1000 * 60 * 45)
                            var expirationDate = new Date(expirationMilliseconds);

                            this.storage.setItem(this.storageKey, this.token, expirationDate);
                            return this.token;
                        }));
                }
                else {
                    return of(null);
                }
            }));
    }    
}
